import React, {
  createContext,
  useEffect,
  useState,
  FC,
  ReactNode,
} from "react";

// ─── Interfaces ──────────────────────────────────────────────────────────────
export interface Settings {
  direction?: "ltr" | "rtl";
  lang: string;
  responsiveFontSizes?: boolean;
  theme: "light" | "dark";
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
}

interface SettingsProviderProps {
  children: ReactNode;
}

// ─── Default Settings ────────────────────────────────────────────────────────
const defaultSettings: Settings = {
  direction: "ltr",
  lang: "en",
  responsiveFontSizes: true,
  theme: "light",
};

// ─── Utils ────────────────────────────────────────────────────────────────────
export const restoreSettings = (): Settings | null => {
  try {
    const stored = localStorage.getItem("settings");
    if (stored) return JSON.parse(stored);
    const prefersDark = matchMedia("(prefers-color-scheme: dark)").matches;
    return { ...defaultSettings, theme: prefersDark ? "dark" : "light" };
  } catch {
    return null;
  }
};

export const storeSettings = (settings: Settings): void =>
  localStorage.setItem("settings", JSON.stringify(settings));

// ─── Context ─────────────────────────────────────────────────────────────────
export const SettingsContext = createContext<SettingsContextValue>({
  settings: defaultSettings,
  saveSettings: () => {},
});

// ─── Provider ────────────────────────────────────────────────────────────────
export const SettingsProvider: FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);

  useEffect(() => {
    const restored = restoreSettings();
    if (restored) setSettings(restored);
  }, []);

  const saveSettings = (update: Settings): void => {
    setSettings(update);
    storeSettings(update);
  };

  return (
    <SettingsContext.Provider value={{ settings, saveSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

// ═══ Exports ════════════════════════════════════════════════════════════════
export const SettingsConsumer = SettingsContext.Consumer;
